import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { formatApiError } from '../lib/api';
import { ArrowRight, Check, X, Loader2 } from 'lucide-react';
import ThemeLock from '../components/ThemeLock';

/**
 * Register — Q36/Q36.1/Q36.2.
 *
 * Hardened against Dark Mode contrast issues and bad UX during sign-up:
 *   • <ThemeLock/> forces ASAP light theme on this screen so iOS/Android
 *     dark-mode users still see legible inputs and the lime CTA.
 *   • Username field: helper text + live availability via
 *     GET /api/auth/check-username (debounced 350ms).
 *   • Password field: live strength indicators (8+ / number / uppercase).
 *   • Primary CTA: black text on lime, weight 600 per Q36 #2.
 */
const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ display_name: '', username: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [usernameState, setUsernameState] = useState({ checking: false, available: null, reason: '' });
  const debounceRef = useRef(null);

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  // Live username availability (debounced)
  useEffect(() => {
    const u = form.username;
    setUsernameState(s => ({ ...s, checking: false, available: null, reason: '' }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!u) return;
    if (!USERNAME_RE.test(u)) {
      setUsernameState({ checking: false, available: false, reason: 'invalid' });
      return;
    }
    setUsernameState({ checking: true, available: null, reason: '' });
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/check-username?u=${encodeURIComponent(u)}`);
        setUsernameState({ checking: false, available: data.available, reason: data.reason });
      } catch {
        setUsernameState({ checking: false, available: null, reason: '' });
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [form.username]);

  // Live password strength
  const pwChecks = useMemo(() => ({
    length: form.password.length >= 8,
    number: /\d/.test(form.password),
    upper: /[A-Z]/.test(form.password),
  }), [form.password]);
  const passwordOk = pwChecks.length && pwChecks.number && pwChecks.upper;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!passwordOk) {
      setError('Password must be 8+ chars with a number and an uppercase letter');
      return;
    }
    if (usernameState.available === false) {
      setError(usernameState.reason === 'taken' ? 'Username already taken' : 'Username invalid');
      return;
    }
    setLoading(true);
    try {
      await register(form.email, form.password, form.username, form.display_name);
      navigate('/onboarding');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="app-container min-h-screen flex flex-col justify-center px-6 bg-[#F5F5F0]"
      data-testid="register-page"
      style={{ colorScheme: 'light' }}
    >
      <ThemeLock />
      <div className="animate-slide-up">
        <h1 className="text-5xl font-black tracking-tighter text-[#111111] leading-none">
          join asap
        </h1>
        <p className="text-[#6B6E73] text-sm mt-2 leading-relaxed">
          anywhere. anytime. any plan.
        </p>

        <form onSubmit={handleSubmit} className="mt-10 space-y-3" noValidate>
          {error && (
            <div data-testid="register-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl px-4 py-3 text-[#FF3B30] text-sm">
              {error}
            </div>
          )}

          {/* Display name */}
          <input
            data-testid="register-name"
            type="text"
            value={form.display_name}
            onChange={e => update('display_name', e.target.value)}
            placeholder="Display Name"
            required
            className="w-full bg-white text-[#111111] rounded-2xl px-4 py-3.5 text-sm border border-black/[0.08] focus:outline-none focus:border-[#6B8A00] placeholder-[#7A7E83] transition-colors"
          />

          {/* Username */}
          <div className="space-y-1.5">
            <div className="relative">
              <input
                data-testid="register-username"
                type="text"
                value={form.username}
                onChange={e => update('username', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                placeholder="Username"
                required
                aria-describedby="username-hint"
                className="w-full bg-white text-[#111111] rounded-2xl px-4 pr-10 py-3.5 text-sm border border-black/[0.08] focus:outline-none focus:border-[#6B8A00] placeholder-[#7A7E83] transition-colors"
              />
              {form.username && (
                <span data-testid="username-status-icon" className="absolute right-3 top-1/2 -translate-y-1/2">
                  {usernameState.checking
                    ? <Loader2 size={16} className="animate-spin text-[#9B9EA3]" />
                    : usernameState.available === true
                      ? <Check size={16} className="text-[#3F5200]" strokeWidth={3} />
                      : usernameState.available === false
                        ? <X size={16} className="text-[#FF3B30]" strokeWidth={3} />
                        : null}
                </span>
              )}
            </div>
            <p
              id="username-hint"
              data-testid="username-hint"
              className={`text-[11px] leading-snug ${
                usernameState.available === false ? 'text-[#FF3B30]'
                : usernameState.available === true ? 'text-[#3F5200] font-bold'
                : 'text-[#7A7E83]'
              }`}
            >
              {usernameState.available === true && '✓ Username available'}
              {usernameState.available === false && usernameState.reason === 'taken' && 'Username already taken'}
              {usernameState.available === false && usernameState.reason === 'invalid' && 'Invalid — see rules below'}
              {usernameState.available === null && '3–20 characters · lowercase letters, numbers and underscores only'}
            </p>
          </div>

          {/* Email */}
          <input
            data-testid="register-email"
            type="email"
            value={form.email}
            onChange={e => update('email', e.target.value)}
            placeholder="Email"
            required
            autoComplete="email"
            className="w-full bg-white text-[#111111] rounded-2xl px-4 py-3.5 text-sm border border-black/[0.08] focus:outline-none focus:border-[#6B8A00] placeholder-[#7A7E83] transition-colors"
          />

          {/* Password */}
          <div className="space-y-2">
            <input
              data-testid="register-password"
              type="password"
              value={form.password}
              onChange={e => update('password', e.target.value)}
              placeholder="Password"
              required
              autoComplete="new-password"
              aria-describedby="password-rules"
              className="w-full bg-white text-[#111111] rounded-2xl px-4 py-3.5 text-sm border border-black/[0.08] focus:outline-none focus:border-[#6B8A00] placeholder-[#7A7E83] transition-colors"
            />
            <div id="password-rules" data-testid="password-rules" className="grid grid-cols-3 gap-1.5">
              <PwRule ok={pwChecks.length} label="8 characters" tid="pw-rule-length" />
              <PwRule ok={pwChecks.number} label="Contains number" tid="pw-rule-number" />
              <PwRule ok={pwChecks.upper} label="Uppercase letter" tid="pw-rule-upper" />
            </div>
          </div>

          {/* Submit — Q36 #2: black text, weight 600 */}
          <button
            data-testid="register-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#B6E600] text-black font-semibold rounded-full py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-[#C8F73D] active:scale-[0.98] transition-all disabled:opacity-50 mt-1"
          >
            {loading ? 'Creating...' : 'Create Account'}
            {!loading && <ArrowRight size={18} strokeWidth={2.6} />}
          </button>
        </form>

        <p className="text-center text-[#6B6E73] text-sm mt-6">
          Already have an account?{' '}
          <Link to="/login" data-testid="go-to-login" className="text-[#3F5200] font-semibold">
            Sign in
          </Link>
        </p>

        <p className="text-center text-[11px] text-[#7A7E83] mt-5">
          By signing up you agree to our{' '}
          <Link to="/terms" data-testid="register-terms-link" className="underline text-[#6B6E73]">Terms</Link>
          {' '}&amp;{' '}
          <Link to="/privacy" data-testid="register-privacy-link" className="underline text-[#6B6E73]">Privacy</Link>.
        </p>
      </div>
    </div>
  );
}

function PwRule({ ok, label, tid }) {
  return (
    <div
      data-testid={tid}
      data-state={ok ? 'ok' : 'unmet'}
      className={`flex items-center gap-1 text-[10.5px] font-bold leading-tight ${
        ok ? 'text-[#3F5200]' : 'text-[#9B9EA3]'
      }`}
    >
      {ok ? <Check size={11} strokeWidth={3} /> : <span className="inline-block w-[11px] h-[11px] rounded-full border border-current" />}
      <span>{label}</span>
    </div>
  );
}
